import { useCallback, useEffect } from "react";
import { filter, includes, map } from "lodash";
import { useCurrentUserLocale } from "@linkfluence/core/user/use-cases";
import { useContextualFocuses } from "@radarly/core/context/use-cases";
import { tr } from "@radarly/i18n";
import { WizardTemplate } from "@radarly/models/insight-pages/wizard/model";
import {
  WizardConfiguration,
  WizardPropertiesConfiguration,
} from "@radarly/models/insight-pages/wizard/model/configuration";
import { useWizardConfigurationForm } from "./useWizardConfigurationForm";

export type Configuration = Omit<WizardConfiguration, "color" | "fields"> & {
  competitors: number[];
};

export function useWizardCompetitors({
  template,
  configuration,
  updateConfiguration,
}: {
  template: WizardTemplate;
  configuration: WizardConfiguration;
  updateConfiguration: (
    configuration: Pick<WizardConfiguration, "fields">
  ) => void;
}) {
  const locale = useCurrentUserLocale();
  const competitors = useContextualFocuses();
  const updateGlobalConfiguration = useCallback(
    (configuration: Configuration) => {
      updateConfiguration({
        ...configuration,
        fields: {
          competitors: configuration.competitors
            ? filter(competitors, ({ id }) =>
                includes(configuration.competitors, id)
              )
            : null,
        },
      });
    },
    [competitors, updateConfiguration]
  );

  const wizardConfiguration: WizardPropertiesConfiguration =
    useWizardConfigurationForm<Configuration>(
      {
        competitors: map(configuration.fields.competitors, "id"),
      },
      updateGlobalConfiguration,
      template
    );

  useEffect(() => {
    if (
      configuration.fields.competitors?.length > 0 &&
      wizardConfiguration.values.competitors.length === 0
    ) {
      updateGlobalConfiguration({
        ...wizardConfiguration.values,
        competitors: [],
      });
    }
  }, [
    configuration.fields.competitors?.length,
    updateGlobalConfiguration,
    wizardConfiguration.values,
  ]);

  return {
    wizardConfiguration,
    size: "m" as const,
    form: {
      title: {
        rules: {
          required: false,
        },
        label: tr(locale, "INSIGHT_PAGE_TEMPLATE_COMPETITORS_TITLE_LABEL"),
        placeholder: tr(
          locale,
          "INSIGHT_PAGE_TEMPLATE_COMPETITORS_PLACEHOLDER"
        ),
      },
      queries: {
        rules: {
          required: true,
        },
        label: tr(
          locale,
          "INSIGHT_PAGE_TEMPLATE_COMPETITORS::CONFIGURATION_QUERIES_LABEL"
        ),
        placeholder: tr(
          locale,
          "INSIGHT_PAGE_TEMPLATE_COMPETITORS::CONFIGURATION_QUERIES_PLACEHOLDER"
        ),
        options: map(competitors, (competitor) => ({
          label: competitor.label,
          value: competitor.id,
        })),
      },
    },
  };
}
